"use client";

import { useState, useEffect } from "react";
import { capturePostHog } from "@/lib/analytics/posthog";
import { cn } from "@/lib/utils";
import { Check, X, Loader2, Sparkles, Shield, CreditCard, TrendingUp, Bitcoin, Zap, CheckCircle2, Settings, RefreshCw } from "lucide-react";
import { CREDITS } from "@/lib/credit-costs";
import { SiteFooter } from "@/components/shared/SiteFooter";
import { toast } from "sonner";
import type {
  Tier,
  BillingInterval,
  SubscriptionState as SubState,
} from "@/lib/subscription-types";

const PLAN_ORDER = ["starter", "builder", "launcher"] as const;

type CTAAction = "subscribe" | "switch" | "upgrade" | "manage" | "reactivate" | "none";

interface CardCTA {
  primaryLabel: string;
  primaryDisabled: boolean;
  primaryAction: CTAAction;
  showCrypto: boolean;
  badge: "current" | "popular" | null;
  subtitle: string | null;
}

const CRYPTO_PAYMENTS_ENABLED = process.env.NEXT_PUBLIC_CRYPTO_PAYMENTS_ENABLED === "true";
const CRYPTO_PAYMENTS_SANDBOX = process.env.NEXT_PUBLIC_CRYPTO_PAYMENTS_SANDBOX === "true";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function getCardCTA(planKey: Tier, isPopular: boolean, isAnnual: boolean, sub: SubState | null): CardCTA {
  // Grace window: subscription was canceled but period_end is still in the future.
  // The /api/subscription/current handler already rewrites canceled+past_end → "expired",
  // so status === "canceled" reliably means "still has access". The periodEnd > now check
  // below is defensive — guards against client/server clock skew or future API changes.
  const periodEndMs = sub?.periodEnd ? new Date(sub.periodEnd).getTime() : null;
  const inGrace =
    sub?.status === "canceled" &&
    !!sub.tier &&
    periodEndMs !== null &&
    periodEndMs > Date.now();

  if (inGrace && sub && planKey === sub.tier) {
    return {
      primaryLabel: sub.periodEnd
        ? `Period ends ${formatDate(sub.periodEnd)} — Reactivate`
        : "Reactivate",
      primaryDisabled: false,
      primaryAction: "reactivate",
      showCrypto: CRYPTO_PAYMENTS_ENABLED,
      badge: "current",
      subtitle: sub.periodEnd ? `Access until ${formatDate(sub.periodEnd)}` : null,
    };
  }

  const hasActive = sub?.status === "active" && sub.tier;

  if (!hasActive || !sub) {
    return {
      primaryLabel: "Subscribe",
      primaryDisabled: false,
      primaryAction: "subscribe",
      showCrypto: CRYPTO_PAYMENTS_ENABLED,
      badge: isPopular ? "popular" : null,
      subtitle: null,
    };
  }

  const currentTier = sub.tier as Tier;
  const currentInterval = sub.interval ?? "monthly";
  const newInterval: BillingInterval = isAnnual ? "annual" : "monthly";
  const isCurrentTier = planKey === currentTier;
  const sameInterval = currentInterval === newInterval;
  const planIdx = PLAN_ORDER.indexOf(planKey);
  const currentIdx = PLAN_ORDER.indexOf(currentTier);

  const renewSubtitle = sub.periodEnd ? `Renews ${formatDate(sub.periodEnd)}` : null;

  if (isCurrentTier && sameInterval) {
    return {
      primaryLabel: "On this plan",
      primaryDisabled: true,
      primaryAction: "none",
      showCrypto: false,
      badge: "current",
      subtitle: renewSubtitle,
    };
  }

  if (isCurrentTier && !sameInterval) {
    return {
      primaryLabel: `Switch to ${newInterval === "annual" ? "Annual" : "Monthly"}`,
      primaryDisabled: false,
      primaryAction: "switch",
      showCrypto: CRYPTO_PAYMENTS_ENABLED,
      badge: "current",
      subtitle: renewSubtitle,
    };
  }

  if (planIdx > currentIdx) {
    return {
      primaryLabel: "Upgrade",
      primaryDisabled: false,
      primaryAction: "upgrade",
      showCrypto: CRYPTO_PAYMENTS_ENABLED,
      badge: isPopular ? "popular" : null,
      subtitle: null,
    };
  }

  // Downgrade — forbidden in pricing UI
  return {
    primaryLabel: "Manage in settings",
    primaryDisabled: false,
    primaryAction: "manage",
    showCrypto: false,
    badge: isPopular ? "popular" : null,
    subtitle: null,
  };
}

/* ------------------------------------------------------------------ */
/*  Data (from central config)                                         */
/* ------------------------------------------------------------------ */

const DISCOUNT = CREDITS.annualDiscountPercent; // 0.2 = 20%
const CRYPTO_BONUS = CREDITS.cryptoBonusPercent; // 0.1 = 10%

const plans = [
  {
    key: "starter" as const,
    name: "Starter",
    monthlyPrice: CREDITS.plans.starter.priceUsd,
    credits: CREDITS.plans.starter.credits,
    tagline: "Turn one validated idea into a real foundation",
    features: [
      `${CREDITS.plans.starter.credits.toLocaleString("en-US")} credits/month`,
      "First idea discovery is free",
      "Business plan generation",
      "Brand kit generation",
      "Session history",
    ],
  },
  {
    key: "builder" as const,
    name: "Builder",
    monthlyPrice: CREDITS.plans.builder.priceUsd,
    credits: CREDITS.plans.builder.credits,
    popular: true,
    tagline: "More iterations, private artifacts, stronger AI",
    features: [
      `${CREDITS.plans.builder.credits.toLocaleString("en-US")} credits/month`,
      "Everything in Starter",
      "Premium AI model option",
      "Private artifact visibility",
      "Default visibility setting",
      "More plan and brand iterations",
    ],
  },
  {
    key: "launcher" as const,
    name: "Launcher",
    monthlyPrice: CREDITS.plans.launcher.priceUsd,
    credits: CREDITS.plans.launcher.credits,
    tagline: "Highest credit budget for serious iteration",
    features: [
      `${CREDITS.plans.launcher.credits.toLocaleString("en-US")} credits/month`,
      "Everything in Builder",
      "Largest monthly credit budget",
      "More room for regenerations",
      "Best for multiple startup directions",
      "Premium AI at scale",
    ],
  },
];

type FeatureRow = {
  label: string;
  starter: boolean | string;
  builder: boolean | string;
  launcher: boolean | string;
};

type CheckoutResponse = {
  ok?: boolean;
  kind?: "noop" | "updated" | "scheduled";
  url?: string;
  error?: string;
  message?: string;
  tier?: Tier | null;
  interval?: BillingInterval | null;
  status?: SubState["status"];
  periodEnd?: string | null;
  pendingTier?: Tier | null;
  pendingAt?: string | null;
};

const redirectToLoginFromPricing = () => {
  window.location.href = `/login?redirect=${encodeURIComponent("/pricing")}`;
};

const checkoutErrorMessage = (data: CheckoutResponse | null, fallback: string) =>
  data?.error || data?.message || fallback;

const comparisonFeatures: FeatureRow[] = [
  { label: "First idea discovery", starter: "Free", builder: "Free", launcher: "Free" },
  { label: "Credits per month", starter: CREDITS.plans.starter.credits.toLocaleString("en-US"), builder: CREDITS.plans.builder.credits.toLocaleString("en-US"), launcher: CREDITS.plans.launcher.credits.toLocaleString("en-US") },
  { label: "Business plan generation", starter: true, builder: true, launcher: true },
  { label: "Brand kit generation", starter: true, builder: true, launcher: true },
  { label: "Session history", starter: true, builder: true, launcher: true },
  { label: "Premium AI (2× credits)", starter: false, builder: true, launcher: true },
  { label: "Private artifacts", starter: false, builder: true, launcher: true },
  { label: "Default privacy", starter: false, builder: true, launcher: true },
  { label: "Iteration room", starter: "Light", builder: "Heavy", launcher: "Max" },
  { label: `Annual billing (−${DISCOUNT * 100}%)`, starter: true, builder: true, launcher: true },
  ...(CRYPTO_PAYMENTS_ENABLED
    ? [{ label: "Crypto bonus (+10%)", starter: true, builder: true, launcher: true }]
    : []),
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function PricingPage() {
  const [loading, setLoading] = useState<string | null>(null);
  const [interval, setInterval] = useState<BillingInterval>("monthly");
  const [sub, setSub] = useState<SubState | null>(null);
  const isAnnual = interval === "annual";

  useEffect(() => {
    capturePostHog("billing.pricing_view", {});
  }, []);

  // Fetch current subscription state on mount.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/subscription/current", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: SubState | null) => {
        if (cancelled || !data) return;
        setSub(data);
        // Default toggle to user's current interval (so «On this plan» / «Reactivate» renders correctly on first paint).
        // Covers both active subscribers and grace-window (canceled but period_end still in future).
        if ((data.status === "active" || data.status === "canceled") && data.interval) {
          setInterval(data.interval);
        }
      })
      // intentional: read-only sub state prefetch; null sub renders pricing page in anonymous mode
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleManage = () => {
    window.location.href = "/settings?tab=billing";
  };

  const getDisplayPrice = (monthlyPrice: number) =>
    isAnnual ? Math.round(monthlyPrice * (1 - DISCOUNT)) : monthlyPrice;

  const getAnnualTotal = (monthlyPrice: number) =>
    Math.round(monthlyPrice * (1 - DISCOUNT)) * 12;

  // Map UI primaryAction → §3.5 checkout_action enum (user intent on click).
  // `subscribe` covers fresh signups; `upgrade` is the cross-tier move
  // (includes `switch` monthly↔annual on the same tier — same conversion
  // intent: paying more for more value); `reactivate` is the grace-window
  // resubscribe button. `manage` / `none` never reach the capture call site
  // because they branch out in the onClick handler above.
  type CheckoutIntent = "subscribe" | "upgrade" | "reactivate";
  const toCheckoutAction = (action: CTAAction): CheckoutIntent => {
    if (action === "reactivate") return "reactivate";
    if (action === "upgrade" || action === "switch") return "upgrade";
    return "subscribe";
  };

  const handleSubscribe = async (tier: string, action: CTAAction = "subscribe") => {
    const plan = plans.find(p => p.key === tier);
    const priceUsd = plan?.monthlyPrice ?? 0;
    const activeCardChange =
      sub?.status === "active" &&
      sub?.paymentProvider === "polar" &&
      (action === "upgrade" || action === "switch");
    capturePostHog("billing.checkout.start", {
      checkout_action: toCheckoutAction(action),
      tier,
      interval,
      price_usd: priceUsd,
      payment_method: "card",
    });
    setLoading(tier);
    try {
      const res = await fetch(activeCardChange ? "/api/subscription/change" : "/api/credits/polar", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(activeCardChange
          ? { targetTier: tier, interval }
          : { pack: tier, interval }),
      });
      if (res.status === 401) {
        toast.error("Please log in to subscribe.");
        redirectToLoginFromPricing();
        return;
      }

      const data = await res.json().catch(() => null) as CheckoutResponse | null;
      if (!res.ok) {
        toast.error(checkoutErrorMessage(data, "Could not start checkout. Try again."));
        return;
      }
      if (activeCardChange && data?.ok) {
        setSub((prev) => prev ? {
          ...prev,
          tier: data.tier ?? prev.tier,
          interval: data.interval ?? prev.interval,
          status: data.status ?? prev.status,
          periodEnd: data.periodEnd ?? prev.periodEnd,
          pendingTier: data.pendingTier ?? null,
          pendingAt: data.pendingAt ?? null,
        } : prev);
        window.dispatchEvent(new Event("credits-updated"));
        toast.success(data.kind === "scheduled" ? "Plan change scheduled" : "Subscription updated");
        return;
      }
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error(activeCardChange
        ? "Subscription update did not complete. Try again."
        : "Checkout did not return a payment link. Try again.");
    } catch {
      toast.error("Network error. Could not start checkout. Try again.");
    } finally {
      setLoading(null);
    }
  };

  const handleCrypto = async (tier: string, action: CTAAction = "subscribe") => {
    if (!CRYPTO_PAYMENTS_ENABLED) return;

    const plan = plans.find(p => p.key === tier);
    const priceUsd = plan?.monthlyPrice ?? 0;
    capturePostHog("billing.checkout.start", {
      checkout_action: toCheckoutAction(action),
      tier,
      interval,
      price_usd: priceUsd,
      payment_method: "crypto",
      payment_mode: CRYPTO_PAYMENTS_SANDBOX ? "sandbox" : "live",
    });
    setLoading(`crypto_${tier}`);
    try {
      const res = await fetch("/api/credits/purchase", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pack: tier, interval }),
      });
      if (res.status === 401) {
        toast.error("Please log in to buy credits.");
        redirectToLoginFromPricing();
        return;
      }

      const data = await res.json().catch(() => null) as CheckoutResponse | null;
      if (!res.ok) {
        toast.error(checkoutErrorMessage(data, "Could not start crypto checkout. Try again."));
        return;
      }
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error("Crypto checkout did not return a payment link. Try again.");
    } catch {
      toast.error("Network error. Could not start crypto checkout. Try again.");
    } finally {
      setLoading(null);
    }
  };

  // Product schema JSON-LD — gives Google rich-result eligibility for each
  // tier (Starter / Builder / Launcher). priceValidUntil is set 1+ year out;
  // bump on the next pricing-page sweep if it gets near expiry.
  const productsJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: plans.map((plan, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      item: {
        "@type": "Product",
        name: `Foundry ${plan.name}`,
        description: plan.tagline,
        offers: {
          "@type": "Offer",
          price: plan.monthlyPrice,
          priceCurrency: "USD",
          priceValidUntil: "2027-12-31",
          availability: "https://schema.org/InStock",
          url: "https://foundrystart.com/pricing",
        },
      },
    })),
  };

  return (
    <>
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(productsJsonLd) }}
    />
    <div className="relative mx-auto w-full min-w-0 max-w-5xl px-4 py-16">
      {/* Ambient amber spotlight behind hero — premium cinema feel */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[640px] bg-[radial-gradient(ellipse_60%_55%_at_top,rgba(251,191,36,0.07),transparent_72%)]"
      />

      {/* ─── Hero ─── */}
      <div className="mb-12 text-center">
        <div className="mb-4 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.24em] text-amber-300/65">
          <span className="h-px w-8 bg-amber-400/40" />
          The Investment
          <span className="h-px w-8 bg-amber-400/40" />
        </div>
        <h1 className="font-heading text-4xl md:text-6xl font-bold leading-[1.05] text-white">
          Choose your forge.
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-lg leading-7 text-white/75">
          Less than a domain. Less than one hour with a consultant.
        </p>
        <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-white/50">
          Enough to find the idea, plan it, brand it, and iterate.
        </p>

        {/* Free-tier — premium chip (mx-auto + flex-wrap so it can shrink/wrap on narrow mobile) */}
        <div className="mx-auto mt-7 flex w-fit max-w-full flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-3xl border border-amber-400/20 bg-amber-400/[0.06] px-4 py-2 text-sm text-amber-200/95">
          <Sparkles className="size-4 shrink-0 text-amber-300" />
          <span>First idea is free. No card needed.</span>
        </div>

        {/* ─── Monthly / Annual toggle ─── */}
        <div className="mt-8 flex items-center justify-center gap-3">
          <span className={cn("text-sm font-medium transition-colors", !isAnnual ? "text-white" : "text-white/45")}>
            Monthly
          </span>
          <button
            onClick={() => setInterval(isAnnual ? "monthly" : "annual")}
            className={cn(
              "relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950",
              isAnnual ? "bg-amber-400" : "bg-white/15",
            )}
            aria-label="Toggle annual billing"
          >
            <span
              className={cn(
                "inline-block size-5 transform rounded-full bg-white shadow-sm transition-transform",
                isAnnual ? "translate-x-8" : "translate-x-1",
              )}
            />
          </button>
          <span className={cn("text-sm font-medium transition-colors", isAnnual ? "text-white" : "text-white/45")}>
            Annual
          </span>
          {isAnnual && (
            <span className="ml-1 inline-flex items-center rounded-full border border-emerald-400/25 bg-emerald-400/[0.08] px-2.5 py-0.5 text-xs font-bold text-emerald-300">
              Save {DISCOUNT * 100}%
            </span>
          )}
        </div>
      </div>

      {CRYPTO_PAYMENTS_ENABLED && (
        <div className="mb-10 mx-auto max-w-xl rounded-xl border-l-2 border-amber-400/60 bg-amber-500/[0.045] px-5 py-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-200">
            <Bitcoin className="size-4 text-amber-300" />
            {CRYPTO_PAYMENTS_SANDBOX
              ? "Crypto sandbox checkout is enabled for testing."
              : `Pay with crypto for +${CRYPTO_BONUS * 100}% bonus credits.`}
          </p>
          <p className="mt-1 text-xs text-white/50">
            {CRYPTO_PAYMENTS_SANDBOX
              ? "NOWPayments sandbox invoices are not live charges."
              : "Bitcoin, Ethereum, USDT, and 100+ cryptocurrencies accepted."}
          </p>
        </div>
      )}

      {/* ─── Pricing Cards ─── */}
      <div className="grid gap-8 md:grid-cols-3 items-stretch">
        {plans.map((plan) => {
          const isPopular = !!plan.popular;
          const displayPrice = getDisplayPrice(plan.monthlyPrice);
          const cta = getCardCTA(plan.key as Tier, isPopular, isAnnual, sub);
          const isCurrent = cta.badge === "current";

          return (
            <div
              key={plan.key}
              className={cn(
                "relative flex flex-col rounded-2xl p-6 min-w-0 transition-[transform,border-color,background-color]",
                isCurrent
                  ? "z-10 md:scale-[1.04] border border-emerald-400/40 bg-emerald-500/[0.05] ring-1 ring-emerald-400/25 shadow-pricing-emerald"
                  : isPopular
                    ? "z-10 md:scale-[1.04] border border-amber-400/40 bg-amber-500/[0.05] ring-1 ring-amber-400/25 shadow-pricing-amber"
                    : "border border-white/10 bg-white/[0.025] hover:border-white/20 hover:bg-white/[0.04]",
              )}
            >
              {/* Top pill — Current Plan (emerald) > Most Popular (amber) > none */}
              {cta.badge === "current" ? (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-emerald-400 px-4 py-1.5 font-heading text-xs font-bold uppercase tracking-[0.14em] text-emerald-950 shadow-[0_0_24px_-4px_rgba(52,211,153,0.7)]">
                  <CheckCircle2 className="size-3" />
                  Current Plan
                </div>
              ) : cta.badge === "popular" ? (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-amber-400 px-4 py-1.5 font-heading text-xs font-bold uppercase tracking-[0.14em] text-zinc-950 shadow-[0_0_24px_-4px_rgba(251,191,36,0.7)]">
                  <Sparkles className="size-3" />
                  Most Popular
                </div>
              ) : null}

              {/* Tier eyebrow — h3 keeps proper hero (h1) → compare (h2) → plan card (h3) hierarchy without visual change */}
              <h3 className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/55">
                {plan.name}
              </h3>

              {/* Price */}
              <div className="mt-3 flex flex-wrap items-baseline gap-x-1.5 gap-y-1 min-w-0">
                <span className="font-heading text-6xl font-bold leading-none tracking-tight text-white">
                  ${displayPrice}
                </span>
                <span className="text-base text-white/50 whitespace-nowrap">/month</span>
              </div>

              {isAnnual ? (
                <div className="mt-2 space-y-0.5">
                  <p className="text-sm text-white/55">
                    <span className="line-through">${plan.monthlyPrice}/mo</span>{" "}
                    <span className="font-medium text-emerald-300">
                      ${getAnnualTotal(plan.monthlyPrice)}/yr
                    </span>
                  </p>
                  <p className="text-xs text-emerald-300/80">
                    You save ${Math.round((plan.monthlyPrice * 12) - getAnnualTotal(plan.monthlyPrice))}/year
                  </p>
                </div>
              ) : (
                <p className="mt-2 text-sm text-white/55">
                  {plan.credits.toLocaleString("en-US")} credits included
                </p>
              )}

              {plan.tagline && (
                <p className="mt-3 text-sm italic leading-5 text-amber-200/80">
                  {plan.tagline}
                </p>
              )}

              {cta.subtitle && (
                <p className="mt-2 text-xs text-emerald-300/85">
                  {cta.subtitle}
                </p>
              )}

              <ul className="mt-6 flex-1 space-y-2.5">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2.5 text-sm text-white/85">
                    <Check className="mt-0.5 size-4 shrink-0 text-emerald-400" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Primary CTA — text adapts to subscription state (Subscribe / Switch / Upgrade / On this plan / Manage) */}
              <button
                type="button"
                onClick={() => {
                  if (cta.primaryAction === "manage") {
                    handleManage();
                  } else if (cta.primaryAction !== "none") {
                    // Active card switch/upgrade uses /api/subscription/change;
                    // subscribe/reactivate still use the checkout flow.
                    // Pass cta.primaryAction so the analytics layer can distinguish
                    // intent (subscribe vs upgrade vs reactivate) — see §3.5 schema.
                    handleSubscribe(plan.key, cta.primaryAction);
                  }
                }}
                disabled={cta.primaryDisabled || loading !== null}
                className={cn(
                  "group mt-7 flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 font-heading text-base font-bold transition-[background-color,border-color,box-shadow,transform]",
                  "focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-400/40 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950",
                  "disabled:cursor-not-allowed disabled:opacity-70",
                  cta.primaryAction === "none"
                    ? "border border-emerald-400/40 bg-emerald-400/[0.06] text-emerald-200"
                    : cta.primaryAction === "manage"
                      ? "border border-white/15 bg-white/[0.03] text-white/70 hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/[0.06] hover:text-white"
                      : isPopular || isCurrent
                        ? "bg-amber-400 text-zinc-950 shadow-[0_0_60px_-16px_rgba(251,191,36,0.85)] hover:-translate-y-0.5 hover:bg-amber-300"
                        : "border border-amber-400/40 bg-amber-400/[0.06] text-amber-200 hover:-translate-y-0.5 hover:border-amber-400 hover:bg-amber-400/15 hover:text-amber-100",
                )}
              >
                {loading === plan.key ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <>
                    {cta.primaryAction === "none" ? (
                      <CheckCircle2 className="size-4" />
                    ) : cta.primaryAction === "manage" ? (
                      <Settings className="size-4" />
                    ) : cta.primaryAction === "reactivate" ? (
                      <RefreshCw className="size-4" />
                    ) : (
                      <CreditCard className="size-4" />
                    )}
                    {cta.primaryLabel}
                  </>
                )}
              </button>

              {/* Pay with Crypto — secondary CTA, hidden если CTA = manage / on-this-plan */}
              {CRYPTO_PAYMENTS_ENABLED && cta.showCrypto && (
              <button
                type="button"
                onClick={() => handleCrypto(plan.key, cta.primaryAction)}
                disabled={loading !== null}
                className={cn(
                  "group mt-3 flex w-full flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-xl border border-amber-500/15 bg-amber-500/[0.045] px-3 py-2.5 text-sm font-bold text-amber-300 transition-[background-color,border-color] sm:px-4",
                  "hover:border-amber-500/35 hover:bg-amber-500/[0.09] hover:text-amber-200",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40",
                  "disabled:cursor-not-allowed disabled:opacity-70",
                )}
              >
                {loading === `crypto_${plan.key}` ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <>
                    <Bitcoin className="size-3.5" />
                    {CRYPTO_PAYMENTS_SANDBOX ? "Crypto Sandbox" : "Pay with Crypto"}
                    <span className="inline-flex items-center gap-0.5 rounded-full border border-amber-400/30 bg-amber-400/[0.12] px-1.5 py-0.5 text-[10px] font-bold text-amber-200">
                      <Zap className="size-2.5" />
                      +{CRYPTO_BONUS * 100}%
                    </span>
                  </>
                )}
              </button>
              )}
            </div>
          );
        })}
      </div>

      {/* ─── Trust badges ─── */}
      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-white/55">
        <span className="inline-flex items-center gap-1.5">
          <Shield className="size-4 text-emerald-400" />
          Cancel anytime, no questions asked
        </span>
        <span className="hidden sm:inline text-white/15">|</span>
        <span className="inline-flex items-center gap-1.5">
          <CreditCard className="size-4 text-emerald-400" />
          {CRYPTO_PAYMENTS_ENABLED
            ? CRYPTO_PAYMENTS_SANDBOX ? "Card & Crypto sandbox" : "Card & Crypto accepted"
            : "Card payments accepted"}
        </span>
        {CRYPTO_PAYMENTS_ENABLED && (
          <>
            <span className="hidden sm:inline text-white/15">|</span>
            <span className="inline-flex items-center gap-1.5">
              <Bitcoin className="size-4 text-amber-300" />
              +{CRYPTO_BONUS * 100}% bonus with crypto
            </span>
          </>
        )}
      </div>

      {/* ─── Feature Comparison Table ─── */}
      <div className="mt-20">
        {/* Section eyebrow */}
        <div className="mb-3 flex items-center justify-center gap-3 text-[11px] font-bold uppercase tracking-[0.24em] text-amber-300/65">
          <span className="h-px w-8 bg-amber-400/40" />
          The Specifics
          <span className="h-px w-8 bg-amber-400/40" />
        </div>
        <h2 className="mb-8 text-center font-heading text-2xl font-bold text-white md:text-3xl">
          Compare plans.
        </h2>

        <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
          <table className="w-full table-fixed text-xs md:table-auto md:text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="w-[40%] py-3 pl-3 pr-2 text-left text-[10px] font-bold uppercase tracking-[0.14em] text-white/45 md:w-auto md:py-4 md:pl-5 md:pr-4 md:text-[11px] md:tracking-[0.16em]">
                  Feature
                </th>
                {plans.map((plan) => (
                  <th
                    key={plan.key}
                    className={cn(
                      "w-[20%] px-1 py-3 text-center font-heading text-[11px] font-bold md:w-auto md:px-4 md:py-4 md:text-base",
                      plan.popular ? "text-amber-300" : "text-white",
                    )}
                  >
                    <span className="inline-flex items-center gap-0.5 md:gap-1.5">
                      {plan.name}
                      {plan.popular && <Sparkles className="hidden size-3 text-amber-300 md:inline" />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonFeatures.map((row, idx) => (
                <tr
                  key={row.label}
                  className={cn(
                    "border-b border-white/5 last:border-b-0",
                    idx % 2 === 1 && "bg-white/[0.012]",
                  )}
                >
                  <td className="break-words py-2.5 pl-3 pr-2 leading-tight text-white/75 md:py-3 md:pl-5 md:pr-4 md:leading-normal">
                    {row.label}
                  </td>
                  {(["starter", "builder", "launcher"] as const).map((tier) => {
                    const value = row[tier];
                    const isPopularCol = tier === "builder";
                    return (
                      <td
                        key={tier}
                        className={cn(
                          "px-2 py-2.5 text-center md:px-4 md:py-3",
                          isPopularCol && "bg-amber-500/[0.025]",
                        )}
                      >
                        {typeof value === "string" ? (
                          <span className={cn("font-medium", isPopularCol ? "text-amber-100" : "text-white")}>
                            {value}
                          </span>
                        ) : value ? (
                          <Check className={cn("mx-auto size-3.5 md:size-4", isPopularCol ? "text-amber-300" : "text-emerald-400")} />
                        ) : (
                          <X className="mx-auto size-3.5 text-white/20 md:size-4" />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ─── Bottom note — editorial wrap-up ─── */}
      <div className="mt-12 space-y-3 text-center">
        <p className="mx-auto max-w-2xl text-sm leading-6 text-white/70">
          Foundry gives you the expensive first pass — idea, plan, brand, and room to iterate.
          <br />
          Pay for what works today. Roadmap features will be priced when they ship.
        </p>
        <p className="inline-flex items-center gap-1.5 text-xs text-white/45">
          <TrendingUp className="size-3" />
          Prices may change as the product expands.
        </p>
        <p className="text-xs italic text-white/40">
          Monthly credits reset each cycle (unused credits don&apos;t roll over). Cancel anytime — no lock-in.
        </p>
      </div>
    </div>
    <SiteFooter variant="full" showCta={false} />
    </>
  );
}
